import { useMemo } from 'react';

import { I18nText } from '../../../../../../../types';
import { UserID } from '../../../../../../users/usersTypes';
import { FetchOperationsFilters } from '../../../../../operationsTypes';
import {
  CompactIndexTableHeaderSortFilterCellFilterField,
  UniversalFilterTypes
} from '../../../../../../common/components/tables/CompactIndexTableHeaderSortFilterCell';

import {
  formsFields,
  operationsKeys,
  words,
  projectsKeys,
  tasksKeys
} from '../../../../../../../locales/keys';

const createdFilterFields: CompactIndexTableHeaderSortFilterCellFilterField<FetchOperationsFilters>[] =
  [
    {
      fieldName: 'createdAt',
      type: UniversalFilterTypes.DATE,
      i18nName: operationsKeys.recordedDate
    }
  ];

const subjectFilterFields: CompactIndexTableHeaderSortFilterCellFilterField<FetchOperationsFilters>[] =
  [
    {
      fieldName: 'subject',
      type: UniversalFilterTypes.TEXT,
      i18nName: operationsKeys.subject
    },
    {
      fieldName: 'body',
      type: UniversalFilterTypes.TEXT,
      i18nName: operationsKeys.description
    },
    {
      fieldName: 'withAttachments',
      type: UniversalFilterTypes.CHECKBOX,
      i18nName: words.attachments
    },
    {
      fieldName: 'projectName',
      type: UniversalFilterTypes.TEXT_PLAIN,
      i18nName: projectsKeys.name
    },
    {
      fieldName: 'taskName',
      type: UniversalFilterTypes.TEXT_PLAIN,
      i18nName: tasksKeys.name
    }
  ];

const stateFilterFields: CompactIndexTableHeaderSortFilterCellFilterField<FetchOperationsFilters>[] =
  [
    {
      fieldName: 'state',
      type: UniversalFilterTypes.OPERATION_STATE,
      i18nName: operationsKeys.status
    }
  ];

const typeFilterFields: CompactIndexTableHeaderSortFilterCellFilterField<FetchOperationsFilters>[] =
  [
    {
      fieldName: 'operationType',
      type: UniversalFilterTypes.OPERATION_TYPE,
      i18nName: operationsKeys.typeOfActivity
    }
  ];

const userFilterFields: CompactIndexTableHeaderSortFilterCellFilterField<FetchOperationsFilters>[] =
  [
    {
      fieldName: 'userId',
      type: UniversalFilterTypes.USERS_EXTENDED,
      i18nName: words.author
    }
  ];

interface CompanyOperationsCompactTableHeaderFiltersOptions {
  clientsIds?: UserID[];
  customI18nCompleteDatetimeTitle?: I18nText;
  customI18nDateTitle?: I18nText;
}

function useCompanyOperationsCompactTableHeaderFilters({
  clientsIds,
  customI18nCompleteDatetimeTitle,
  customI18nDateTitle
}: CompanyOperationsCompactTableHeaderFiltersOptions) {
  const clientFilterFields = useMemo<
    | CompactIndexTableHeaderSortFilterCellFilterField<FetchOperationsFilters>[]
    | null
  >(
    () =>
      clientsIds
        ? [
            {
              fieldName: 'clientId',
              type: UniversalFilterTypes.USERS_EXTENDED,
              i18nName: formsFields.user,
              options: {
                withoutRecentUsers: true,
                initialUsersFilters: {
                  id: { in: clientsIds }
                }
              }
            }
          ]
        : null,
    [clientsIds]
  );

  const completedFilterFields = useMemo<
    | CompactIndexTableHeaderSortFilterCellFilterField<FetchOperationsFilters>[]
    | null
  >(
    () => [
      {
        fieldName: 'completeDatetime',
        type: UniversalFilterTypes.DATE,
        i18nName:
          customI18nCompleteDatetimeTitle || operationsKeys.completedDate
      }
    ],
    [customI18nCompleteDatetimeTitle]
  );

  const dateFilterFields = useMemo<
    | CompactIndexTableHeaderSortFilterCellFilterField<FetchOperationsFilters>[]
    | null
  >(
    () => [
      {
        fieldName: 'date',
        type: UniversalFilterTypes.DATE,
        i18nName: customI18nDateTitle || operationsKeys.dueDate
      }
    ],
    [customI18nDateTitle]
  );

  return {
    clientFilterFields,
    createdFilterFields,
    dateFilterFields,
    completedFilterFields,
    subjectFilterFields,
    stateFilterFields,
    typeFilterFields,
    userFilterFields
  };
}

export default useCompanyOperationsCompactTableHeaderFilters;
